/**
 * What the app knows about the deployment's API keys.
 *
 * Booleans, and nothing else. This store used to hold a visitor's own keys in
 * local storage and drive a Settings panel that could write the owner's keys
 * too; both are gone. Keys are set at /admin, behind a password, and the rest
 * of the app only needs to know which features are switched on.
 *
 * `status` is null until the first fetch answers, which is why consumers
 * treat null as "not yet known" rather than "missing" — a notice that flashed
 * on load and then vanished would be worse than one arriving a moment late.
 */
import { create } from "zustand";

import { type ServerKeyName, type ServerStatus, fetchServerStatus } from "@/lib/keys";
import { cesiumIonToken } from "@/lib/publicConfig";

type KeysState = {
  /** Which keys the backend reports as set. `null` until the first fetch. */
  status: ServerStatus | null;
  /** The backend could not be reached on the last status fetch. */
  offline: boolean;
  /** Refresh the status. Safe to call repeatedly; sends nothing, writes nothing. */
  sync: () => Promise<void>;
};

export const useKeysStore = create<KeysState>((set) => ({
  status: null,
  offline: false,

  sync: async () => {
    try {
      set({ status: await fetchServerStatus(), offline: false });
    } catch {
      set({ offline: true });
    }
  },
}));

/** Is one of the deployment's keys missing? False while status is unknown. */
export function useKeyMissing(name: ServerKeyName): boolean {
  return useKeysStore((s) => s.status !== null && s.status.keys[name] === false);
}

/** The deployment's Cesium token; the globe cannot mount without it.
 *
 * Reads the config fetched before render rather than the store, because the
 * token is no longer per-visitor. Not reactive, and does not need to be —
 * Cesium binds its token at module load, so the admin panel reloads the tab
 * after changing it rather than trying to re-authenticate a live viewer.
 */
export function useHasCesiumToken(): boolean {
  return Boolean(cesiumIonToken());
}
